/* eslint-disable react/jsx-props-no-spreading */
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles } from '@material-ui/styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ButtonWithArrow from '../components/Buttons/ButtonWithArrow';
import Footer from '../components/Footer/Footer';
import Md from '../components/Md';
import { MainHeading, NormalText, SubtleText } from '../components/typography';
import PropTypes, { customPagesPropType } from '../helpers/propTypes';
import useId from '../hooks/useId';
import usePageAira from '../hooks/usePageAria';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflowY: 'auto',
  },

  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  image: {
    width: '100%',
    height: 260,
    flexShrink: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',

    [theme.breakpoints.down('xs')]: {
      height: 120,
    },
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: 600,
    maxWidth: '90%',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },

  header: {
    marginTop: theme.spacing(3),
  },

  buttonContainer: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: theme.spacing(5),
    marginBottom: 0,
  },
}));

function ParticipateText() {
  return (
    <FormattedMessage
      defaultMessage="I want to participate"
      id="Welcome.Participate"
    />
  );
}

function Welcome({
  navigateModal,
  closeModal,
  welcomeImage,
  contentId,
  customPages: { welcome: customPage = {} },
}) {
  const clipPathId = useId();
  const aria = usePageAira({ noFocus: true });

  const theme = useTheme();
  const footer = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();

  const buttonText = customPage.buttonText || <ParticipateText />;
  const onClickHandler = () =>
    customPage.buttonAction
      ? closeModal(customPage.buttonAction)
      : navigateModal({ forward: true });

  return (
    <>
      <svg height="0" width="0">
        tagstags
        <defs>
          <clipPath clipPathUnits="objectBoundingBox" id={clipPathId}>
            <path d="m 0,-8e-8 v 0.9455507 C 0,0.94555062 0.22434471,1 0.50025839,1 0.77617207,1 1,0.94555062 1,0.94555062 V -8e-8 Z" />
          </clipPath>
        </defs>
      </svg>
      <div className={classes.root} {...aria}>
        <div className={classes.container}>
          <div
            className={classes.image}
            style={{
              backgroundImage: `url(${welcomeImage})`,
              clipPath: `url(#${clipPathId})`,
            }}
          />
          <div className={classes.content}>
            <MainHeading
              className={classes.header}
              underline="true"
              variant="h2"
            >
              {customPage.title || (
                <FormattedMessage
                  defaultMessage="Find out if this study is a match for you"
                  id="Welcome.FindOutIfQualify"
                />
              )}
            </MainHeading>
            <NormalText>
              {customPage.text ? (
                <Md tags={customPage.tags}>{customPage.text}</Md>
              ) : (
                <FormattedMessage
                  defaultMessage="To sign up for this study, please answer a few questions and let us know which clinic you would like to visit during the study."
                  id="Welcome.ToSignUp"
                />
              )}
            </NormalText>
            {customPage.subText && (
              <SubtleText variant="subtitle1">
                <Md tags={customPage.tags}>{customPage.subText}</Md>
              </SubtleText>
            )}
            {!footer && (
              <div className={classes.buttonContainer}>
                <ButtonWithArrow
                  aria-controls={contentId}
                  color="primary"
                  onClick={onClickHandler}
                  variant="contained"
                >
                  {buttonText}
                </ButtonWithArrow>
              </div>
            )}
          </div>
        </div>
      </div>
      {footer && (
        <Footer
          formIsComplete
          footerText={buttonText}
          onContinueClick={onClickHandler}
        />
      )}
    </>
  );
}

Welcome.propTypes = {
  closeModal: PropTypes.func.isRequired,
  contentId: PropTypes.string.isRequired,
  customPages: customPagesPropType,
  navigateModal: PropTypes.func.isRequired,
  welcomeImage: PropTypes.string,
};

Welcome.defaultProps = {
  customPages: {},
  welcomeImage: undefined,
};

export default Welcome;
